/**
 * Telegram Accounts API Service (KAN-592, KAN-601)
 *
 * Thin axios wrapper:
 *   GET    /api/v1/telegram-accounts/               - list
 *   GET    /api/v1/telegram-accounts/gateway/       - gateway status
 *   POST   /api/v1/telegram-accounts/login/start/   - request login code
 *   POST   /api/v1/telegram-accounts/login/confirm/ - confirm code (+2FA)
 *   POST   /api/v1/telegram-accounts/import/        - import session (multipart)
 *   POST   /api/v1/telegram-accounts/archive/inspect/ - inspect seller zip (KAN-722, multipart)
 *   POST   /api/v1/telegram-accounts/import-batch/plan/ - preview a batch of zips (KAN-776, multipart)
 *   POST   /api/v1/telegram-accounts/import-batch/      - start a batch import (KAN-776, multipart, 202)
 *   GET    /api/v1/telegram-accounts/import-batch/{batch_id}/ - batch progress (KAN-776)
 *   PATCH  /api/v1/telegram-accounts/{id}/          - enable / disable / outreach
 *   DELETE /api/v1/telegram-accounts/{id}/          - remove
 *   POST   /api/v1/telegram-accounts/{id}/check/    - health check (KAN-613)
 *   POST   /api/v1/telegram-accounts/{id}/auth-check/ - проверка авторизации (KAN-848)
 *   POST   /api/v1/telegram-accounts/{id}/profile/  - refresh profile (KAN-618)
 *   PATCH  /api/v1/telegram-accounts/{id}/profile/  - edit profile (KAN-623, multipart)
 *   GET    /api/v1/telegram-accounts/{id}/video/    - video: JSON {url} for S3, the file itself for local storage (KAN-648, KAN-785)
 *   POST   /api/v1/telegram-accounts/{id}/video/    - upload the video file (KAN-648, multipart)
 *   DELETE /api/v1/telegram-accounts/{id}/video/    - remove the video file (KAN-648)
 *   POST   /api/v1/telegram-accounts/{id}/send/     - message to any recipient or self (KAN-649)
 *   PATCH  /api/v1/telegram-accounts/{id}/proxy/    - change / remove proxy (KAN-650)
 */
import apiClient from './client';
import { fetchApiFile } from './apiFiles';
import {
  buildBatchImportFormData,
  buildBatchPlanFormData,
  type BatchImportFields,
} from '@/lib/telegramImportBatch';
import { videoResponseToSource, type VideoSource } from '@/lib/accountVideo';
import type {
  ArchiveInspectResult,
  BatchImportStatus,
  BatchPlanResult,
  BatchStartResult,
  GatewayStatus,
  ImportResult,
  LoginConfirmBody,
  LoginConfirmResult,
  LoginStartBody,
  LoginStartResult,
  AuthCheckResult,
  CheckHealthResult,
  DirectSendBody,
  DirectSendResult,
  ProxyChangeBody,
  ProxyChangeResult,
  TelegramAccount,
  UpdateAccountBody,
  UpdateAccountResult,
} from '@/types/telegramAccount';

const BASE = '/api/v1/telegram-accounts';

export const telegramAccountsApi = {
  /**
   * List accounts (RBAC-scoped server-side). Accepts both a bare array and a
   * paginated `{ results: [...] }` object.
   */
  list: async (): Promise<TelegramAccount[]> => {
    const response = await apiClient.get(`${BASE}/`);
    const d = response.data;
    return Array.isArray(d) ? d : (d?.results ?? []);
  },

  gatewayStatus: async (): Promise<GatewayStatus> => {
    const { data } = await apiClient.get<GatewayStatus>(`${BASE}/gateway/`);
    return data;
  },

  loginStart: async (body: LoginStartBody): Promise<LoginStartResult> => {
    const { data } = await apiClient.post<LoginStartResult>(
      `${BASE}/login/start/`,
      body
    );
    return data;
  },

  loginConfirm: async (body: LoginConfirmBody): Promise<LoginConfirmResult> => {
    const { data } = await apiClient.post<LoginConfirmResult>(
      `${BASE}/login/confirm/`,
      body
    );
    return data;
  },

  importAccount: async (formData: FormData): Promise<ImportResult> => {
    const { data } = await apiClient.post<ImportResult>(
      `${BASE}/import/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  /**
   * KAN-677: один zip от продавца (папка tdata или .session и .json), формат
   * определяет сервер; в ответе detected и, возможно, warning.
   */
  importArchive: async (formData: FormData): Promise<ImportResult> => {
    const { data } = await apiClient.post<ImportResult>(
      `${BASE}/import-archive/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  /**
   * KAN-722: разобрать zip до импорта: формат, номер, 2FA, подобранная метка.
   * FormData с тем же полем archive, что у import-archive/.
   */
  inspectArchive: async (formData: FormData): Promise<ArchiveInspectResult> => {
    const { data } = await apiClient.post<ArchiveInspectResult>(
      `${BASE}/archive/inspect/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  },

  /**
   * KAN-776: предпросмотр партии архивов: номер, страна, 2FA и прокси на
   * каждый файл, сводка по странам. Файлы уходят полем archives.
   */
  planBatchImport: async (
    files: File[],
    operatorId?: string
  ): Promise<BatchPlanResult> => {
    const { data } = await apiClient.post<BatchPlanResult>(
      `${BASE}/import-batch/plan/`,
      buildBatchPlanFormData(files, operatorId),
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 }
    );
    return data;
  },

  /** KAN-776: запустить партию (202, batch_id); ход смотреть в getBatchImport. */
  startBatchImport: async (
    files: File[],
    fields: Omit<BatchImportFields, 'archives'>
  ): Promise<BatchStartResult> => {
    const { data } = await apiClient.post<BatchStartResult>(
      `${BASE}/import-batch/`,
      buildBatchImportFormData({ ...fields, archives: files }),
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 0 }
    );
    return data;
  },

  /** KAN-776: ход партии; опрашивается, пока status running. */
  getBatchImport: async (batchId: string): Promise<BatchImportStatus> => {
    const { data } = await apiClient.get<BatchImportStatus>(
      `${BASE}/import-batch/${encodeURIComponent(batchId)}/`
    );
    return data;
  },

  updateAccount: async (
    accountId: string,
    body: UpdateAccountBody,
    operatorId?: string
  ): Promise<UpdateAccountResult> => {
    const { data } = await apiClient.patch<{
      account: UpdateAccountResult;
      holder_synced?: boolean;
    }>(`${BASE}/${encodeURIComponent(accountId)}/`, body, {
      params: operatorId ? { operator_id: operatorId } : undefined,
    });
    // KAN-617: holder_synced может прийти рядом с account или внутри него
    return typeof data.holder_synced === 'boolean'
      ? { ...data.account, holder_synced: data.holder_synced }
      : data.account;
  },

  setEnabled: (
    accountId: string,
    enabled: boolean,
    force?: boolean,
    operatorId?: string
  ): Promise<TelegramAccount> =>
    telegramAccountsApi.updateAccount(
      accountId,
      force ? { enabled, force: true } : { enabled },
      operatorId
    ),

  checkAccount: async (
    accountId: string,
    operatorId?: string
  ): Promise<CheckHealthResult> => {
    const { data } = await apiClient.post<CheckHealthResult>(
      `${BASE}/${encodeURIComponent(accountId)}/check/`,
      undefined,
      { params: operatorId ? { operator_id: operatorId } : undefined }
    );
    return data;
  },

  /** KAN-848: проверить авторизацию сессии (любой статус, кроме свежей сессии). */
  authCheck: async (
    accountId: string,
    operatorId?: string
  ): Promise<AuthCheckResult> => {
    const { data } = await apiClient.post<AuthCheckResult>(
      `${BASE}/${encodeURIComponent(accountId)}/auth-check/`,
      undefined,
      { params: operatorId ? { operator_id: operatorId } : undefined }
    );
    return data;
  },

  /** KAN-618: перечитать профиль (имя, username, аватар) из Telegram. */
  refreshProfile: async (
    accountId: string,
    operatorId?: string
  ): Promise<TelegramAccount> => {
    const { data } = await apiClient.post<{ account: TelegramAccount }>(
      `${BASE}/${encodeURIComponent(accountId)}/profile/`,
      undefined,
      { params: operatorId ? { operator_id: operatorId } : undefined }
    );
    return data.account;
  },

  /**
   * KAN-623: изменить профиль в Telegram. FormData: first_name, last_name,
   * about, photo (файл), remove_photo ("true").
   */
  updateProfile: async (
    accountId: string,
    formData: FormData,
    operatorId?: string
  ): Promise<TelegramAccount> => {
    const { data } = await apiClient.patch<{ account: TelegramAccount }>(
      `${BASE}/${encodeURIComponent(accountId)}/profile/`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: operatorId ? { operator_id: operatorId } : undefined,
      }
    );
    return data.account;
  },

  /** KAN-648: загрузить файл видео аккаунта (video/*, до 200 МБ). */
  uploadVideo: async (
    accountId: string,
    file: File,
    operatorId?: string
  ): Promise<void> => {
    const fd = new FormData();
    fd.append('video', file);
    await apiClient.post(`${BASE}/${encodeURIComponent(accountId)}/video/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: operatorId ? { operator_id: operatorId } : undefined,
      timeout: 0,
    });
  },

  /** KAN-649: сообщение с аккаунта получателю (@username, телефон или "self"); ставится в очередь. */
  sendMessage: async (
    accountId: string,
    body: DirectSendBody,
    operatorId?: string
  ): Promise<DirectSendResult> => {
    const payload = operatorId ? { ...body, operator_id: operatorId } : body;
    const { data } = await apiClient.post<DirectSendResult>(
      `${BASE}/${encodeURIComponent(accountId)}/send/`,
      payload
    );
    return data;
  },

  /** KAN-650: сменить или убрать прокси аккаунта без повторного входа. */
  changeProxy: async (
    accountId: string,
    body: ProxyChangeBody,
    operatorId?: string
  ): Promise<ProxyChangeResult> => {
    const payload = operatorId ? { ...body, operator_id: operatorId } : body;
    const { data } = await apiClient.patch<ProxyChangeResult>(
      `${BASE}/${encodeURIComponent(accountId)}/proxy/`,
      payload,
      { timeout: 90_000 }
    );
    return data;
  },

  /** KAN-648: убрать файл видео (ссылка video_url остаётся). */
  deleteVideo: async (accountId: string, operatorId?: string): Promise<void> => {
    await apiClient.delete(`${BASE}/${encodeURIComponent(accountId)}/video/`, {
      params: operatorId ? { operator_id: operatorId } : undefined,
    });
  },

  /**
   * KAN-648, KAN-785: видео аккаунта после проверки прав. S3: JSON {url}
   * (presigned адрес); локальное хранилище: сам файл (Content-Type video/*).
   */
  videoFile: async (
    accountId: string,
    operatorId?: string
  ): Promise<VideoSource> => {
    const response = await apiClient.get<Blob>(
      `${BASE}/${encodeURIComponent(accountId)}/video/`,
      {
        params: operatorId ? { operator_id: operatorId } : undefined,
        responseType: 'blob',
        timeout: 0,
      }
    );
    const contentType = response.headers?.['content-type'] as string | undefined;
    return videoResponseToSource(contentType, response.data);
  },

  /** KAN-618: аватар с закрытого эндпоинта (нужен токен, голый img не подходит). */
  fetchAvatar: (url: string): Promise<Blob> => fetchApiFile(url),
};

export default telegramAccountsApi;
